
import { requestJson } from './api';

const MAX_QUERY_LENGTH = 490; // Keep it safely under the 500-char limit of the MyMemory API.
const MAX_CONCURRENT_REQUESTS = 5;

interface TranslationApiResponse {
  responseData?: {
    translatedText?: string;
  };
  responseStatus?: number;
  responseDetails?: string;
}

/**
 * Makes a single API call to the MyMemory API.
 * @param text The text to translate (must be under the length limit).
 * @param langPair The language pair string, e.g., 'de|en'.
 * @returns A promise that resolves to the translated text.
 */
const callApi = async (text: string, langPair: string): Promise<string> => {
  const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${langPair}`;
  try {
    const data = await requestJson<TranslationApiResponse>(
      url,
      { credentials: 'omit' },
      'Übersetzungsanfrage fehlgeschlagen.'
    );

    if (!data.responseData || typeof data.responseData.translatedText !== 'string') {
      throw new Error('Ungültige JSON-Antwort der Übersetzungs-API.');
    }

    if (data.responseStatus !== 200) {
      // The API returns a 200 OK HTTP status even for internal errors, so we check the response body.
      throw new Error(`Übersetzungs-API-Fehler: ${data.responseDetails || 'Unbekannter Fehler.'}`);
    }

    // The actual translated text is in this field.
    return data.responseData.translatedText;
  } catch (error) {
    console.error('MyMemory API call failed.');
    // Let the main function handle the user-facing error message.
    throw error;
  }
};


interface ChunkWithSeparator {
  text: string;
  separatorAfter: string;
}

export const chunkText = (text: string, maxLength = MAX_QUERY_LENGTH): ChunkWithSeparator[] => {
  const result: ChunkWithSeparator[] = [];
  let start = 0;
  while (start < text.length) {
    if (text.length - start <= maxLength) {
      result.push({ text: text.slice(start), separatorAfter: '' });
      break;
    }
    const end = start + maxLength;
    let splitPos = -1;
    let sepLen = 0;

    const lastPara = text.lastIndexOf('\n\n', end);
    if (lastPara > start) {
      splitPos = lastPara;
      sepLen = 2;
    } else {
      const lastLine = text.lastIndexOf('\n', end);
      if (lastLine > start) {
        splitPos = lastLine;
        sepLen = 1;
      } else {
        const lastSpace = text.lastIndexOf(' ', end);
        if (lastSpace > start) {
          splitPos = lastSpace;
          sepLen = 1;
        }
      }
    }

    if (splitPos > start) {
      const chunk = text.slice(start, splitPos);
      const sep = text.slice(splitPos, splitPos + sepLen);
      result.push({ text: chunk, separatorAfter: sep });
      start = splitPos + sepLen;
    } else {
      result.push({ text: text.slice(start, end), separatorAfter: '' });
      start = end;
    }
  }
  return result;
};

/**
 * Translates text using the MyMemory Translated API.
 * This function handles texts longer than the API's character limit by splitting them into chunks.
 * @param text The text to translate.
 * @param sourceLang The source language code (e.g., 'de' for German).
 * @param targetLang The target language code (e.g., 'en' for English).
 * @param appendInstruction If true, appends a German instruction regardless of target language.
 * @returns A promise that resolves to the translated text.
 */
export const translateText = async (
  text: string,
  sourceLang: string,
  targetLang: string,
  appendInstruction = false
): Promise<string> => {
  if (!text.trim()) {
    return '';
  }
  
  const langPair = `${sourceLang}|${targetLang}`;

  try {
    let translatedText: string;

    // If the text is short enough, make a single request to avoid unnecessary processing.
    if (text.length <= MAX_QUERY_LENGTH) {
      translatedText = await callApi(text, langPair);
    } else {
      // Split into chunks preserving paragraph and word boundaries
      const chunkItems = chunkText(text, MAX_QUERY_LENGTH);
      const translatedItems: string[] = [];

      // Translate chunks with limited concurrency to avoid hitting rate limits.
      for (let i = 0; i < chunkItems.length; i += MAX_CONCURRENT_REQUESTS) {
        const batch = chunkItems.slice(i, i + MAX_CONCURRENT_REQUESTS);
        const results = await Promise.all(
          batch.map(item => (item.text.trim() ? callApi(item.text, langPair) : Promise.resolve(item.text)))
        );
        translatedItems.push(...results);
      }

      // Join the translated chunks back together with their original separators.
      translatedText = chunkItems
        .map((item, idx) => translatedItems[idx] + item.separatorAfter)
        .join('');
    }
    
    const shouldAppendInstruction = targetLang === 'de' || appendInstruction;
    if (shouldAppendInstruction && translatedText && translatedText.trim()) {
        return `${translatedText}\n\n---\nAnweisung: Bitte antworte auf Deutsch.`;
    }

    return translatedText; // Return original if translation is empty or instruction not needed
    
  } catch (error) {
    console.error('Translation failed.');
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unbekannter Fehler bei der Übersetzung.');
  }
};
